import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { ArrowUp, Plus } from "lucide-react";
import type {
  ClaimAddRequest,
  ClaimStrategyResponse,
  UnplacedPositionResponse,
} from "@patent-studio/api-types";
import { Menu, MenuGroup, MenuItem, MenuSeparator } from "@/components/ui/Menu";
import { useAddClaim } from "@/hooks/use-claim-strategy";
import { tabPath } from "../stage-vocabulary";
import { Refusal } from "./ChainControls";
import { markPlacedTagging, placedWords } from "./claim-lanes";
import { useClaimSetMoments } from "./claim-set-moments";

/**
 * Where a position can go, in the menu that offered it: as a dependent of any
 * independent claim, listed in claim order with its category, or as a new
 * independent claim that Praxis tags from its words. Mounted only while the
 * menu is open, so each opening starts from the first choice.
 */
function Choices({
  stage,
  editable,
  place,
  goToNovelty,
}: {
  stage: ClaimStrategyResponse;
  editable: boolean;
  place: (request: Omit<ClaimAddRequest, "position_id">) => void;
  goToNovelty: () => void;
}) {
  const [placing, setPlacing] = useState(false);
  if (!placing) {
    return (
      <>
        {editable && (
          <>
            <MenuItem
              title="Place within current claim set"
              description="Select the claim this position should sit under, or make it a new claim."
              keepOpen
              onSelect={() => {
                setPlacing(true);
              }}
            />
            <MenuSeparator />
          </>
        )}
        <MenuItem
          title="See at Novelty"
          description="Opens the position's verdict and the prior art it was read against."
          onSelect={goToNovelty}
        />
      </>
    );
  }
  const category = new Map(
    stage.forms.map((form) => [form.form_id, form.name]),
  );
  return (
    // The design caps the list and scrolls it, so a long set stays on screen.
    <div className="max-h-[290px] overflow-y-auto">
      <MenuGroup heading="As dependent of">
        {stage.claims
          .filter((claim) => claim.kind === "independent")
          .map((claim) => (
            <MenuItem
              key={claim.claim_id}
              icon={<ArrowUp aria-hidden />}
              title={`Claim ${String(claim.number)}`}
              description={category.get(claim.form_id)}
              onSelect={() => {
                place({ parent_claim_id: claim.claim_id });
              }}
            />
          ))}
      </MenuGroup>
      <MenuItem
        icon={<Plus aria-hidden />}
        title="As new independent claim"
        brand
        onSelect={() => {
          place({});
        }}
      />
    </div>
  );
}

/**
 * The acts on a position waiting to be placed, in the design's menu: while the
 * stage is open, place it within the claim set, choosing where from a list
 * inside the menu; and go to where its verdict is kept. A placement opens the
 * form it landed in and says which claim it became; a refusal stays in the
 * server's words.
 */
export function PositionOptions({
  taskId,
  position,
  stage,
  editable,
}: {
  taskId: string;
  position: UnplacedPositionResponse;
  stage: ClaimStrategyResponse;
  /** Whether the stage is open to writes, so the position can be placed. */
  editable: boolean;
}) {
  const add = useAddClaim(taskId);
  const navigate = useNavigate();
  const { markTagging } = useClaimSetMoments();
  const place = (where: Omit<ClaimAddRequest, "position_id">) => {
    const request = { ...where, position_id: position.position_id };
    add.mutate(request, {
      onSuccess: (after) => {
        markPlacedTagging(stage, after, markTagging);
        toast(placedWords(stage, after, request));
      },
    });
  };
  return (
    <>
      <Menu name={`Options for position ${String(position.number)}`}>
        <Choices
          stage={stage}
          editable={editable}
          place={place}
          goToNovelty={() => {
            void navigate(tabPath(taskId, "thesis", "novelty", "analysis"));
          }}
        />
      </Menu>
      {/* On a line of its own under the row the menu sits in. */}
      {add.isError && (
        <div className="basis-full">
          <Refusal error={add.error} />
        </div>
      )}
    </>
  );
}
